/**
 * Session-boundary decisions for reconciling a fetched message batch into the shared message store.
 *
 * Both predicates exist because the obvious comparison, "does the batch's session differ from
 * `currentSessionId`", does not work. `currentSessionId` tracks the session that was REQUESTED and
 * is advanced the moment the sessionId prop changes, so by the time a batch arrives it already
 * equals the new session and any guard built on it is dead. These compare against the batch and the
 * store instead, both of which lag the request in the same way the data does.
 */

/**
 * True when a fetched batch belongs to a session other than the one explicitly requested.
 *
 * While SWR revalidates after a session switch it can still hold the previous session's batch.
 * Merging that writes the old conversation into the new session's view, and those messages survive
 * de-duplication because they match none of the new session's ids or values.
 *
 * Only applies when a session was named explicitly: with no requested session the caller is asking
 * for "whatever the latest session is", and the batch defines the answer rather than contradicting it.
 */
export function isStaleBatch(requestedSessionId: string | undefined, fetchedSessionId: string | undefined): boolean {
  if (!requestedSessionId || !fetchedSessionId) return false
  return fetchedSessionId !== requestedSessionId
}

/**
 * True when the store holds a different session's messages and must be replaced rather than merged.
 *
 * An undefined store session means nothing has been loaded yet, so there is nothing to discard.
 */
export function storeHoldsAnotherSession(storeSessionId: string | undefined, effectiveSessionId: string | undefined): boolean {
  if (storeSessionId === undefined) return false
  return storeSessionId !== effectiveSessionId
}
